export const PORTAL_VIEW_AS_STORAGE_KEY = 'portalViewAsSession'
export const PORTAL_VIEW_AS_PENDING_TICKET_KEY = 'portalViewAsPendingTicketId'
export const PORTAL_VIEW_AS_PENDING_TOKEN_KEY = 'portalViewAsPendingToken'
export const PORTAL_VIEW_AS_LOCAL_FUNCTIONS_PARAM = 'functions'
export const PORTAL_VIEW_AS_LOCAL_FUNCTIONS_VALUE = 'local'

export type PortalViewAsStoredSession = {
  ticketId: string
  enrollmentUserId: string
  expiresAt: string
  clientId?: string
}

export const parsePortalViewAsSession = (raw: string | null): PortalViewAsStoredSession | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PortalViewAsStoredSession>
    if (
      typeof parsed.ticketId !== 'string' ||
      parsed.ticketId.trim().length === 0 ||
      typeof parsed.enrollmentUserId !== 'string' ||
      parsed.enrollmentUserId.trim().length === 0 ||
      typeof parsed.expiresAt !== 'string' ||
      parsed.expiresAt.trim().length === 0
    ) {
      return null
    }
    const clientId =
      typeof parsed.clientId === 'string' && parsed.clientId.trim().length > 0 ? parsed.clientId.trim() : undefined
    return {
      ticketId: parsed.ticketId.trim(),
      enrollmentUserId: parsed.enrollmentUserId.trim(),
      expiresAt: parsed.expiresAt.trim(),
      ...(clientId ? { clientId } : {})
    }
  } catch {
    return null
  }
}

export const isPortalViewAsSessionExpired = (expiresAt: string, nowMs = Date.now()): boolean => {
  const expiresMs = Date.parse(expiresAt)
  if (Number.isNaN(expiresMs)) return true
  return expiresMs <= nowMs
}

export const readPortalViewAsSession = (): PortalViewAsStoredSession | null => {
  try {
    return parsePortalViewAsSession(sessionStorage.getItem(PORTAL_VIEW_AS_STORAGE_KEY))
  } catch {
    return null
  }
}

export const writePortalViewAsSession = (session: PortalViewAsStoredSession): void => {
  try {
    sessionStorage.setItem(PORTAL_VIEW_AS_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // ignore quota / private mode
  }
}

export const clearPortalViewAsSession = (): void => {
  try {
    sessionStorage.removeItem(PORTAL_VIEW_AS_STORAGE_KEY)
  } catch {
    // ignore quota / private mode
  }
}

export const parseViewAsTokenFromHash = (hash: string): string | null => {
  if (!hash) return null
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  if (!raw) return null
  if (raw.startsWith('token=')) {
    const token = raw.slice('token='.length)
    return token ? decodeURIComponent(token) : null
  }
  return decodeURIComponent(raw)
}

export const shouldUseLocalFunctionsFromSearch = (search: string): boolean => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  return params.get(PORTAL_VIEW_AS_LOCAL_FUNCTIONS_PARAM) === PORTAL_VIEW_AS_LOCAL_FUNCTIONS_VALUE
}

export const readPendingPortalViewAsToken = (): string | null => {
  try {
    const token = sessionStorage.getItem(PORTAL_VIEW_AS_PENDING_TOKEN_KEY)
    return token && token.length > 0 ? token : null
  } catch {
    return null
  }
}

export const writePendingPortalViewAsToken = (token: string): void => {
  try {
    sessionStorage.setItem(PORTAL_VIEW_AS_PENDING_TOKEN_KEY, token)
  } catch {
    // ignore quota / private mode
  }
}

export const clearPendingPortalViewAsToken = (): void => {
  try {
    sessionStorage.removeItem(PORTAL_VIEW_AS_PENDING_TOKEN_KEY)
  } catch {
    // ignore quota / private mode
  }
}

export const consumeViewAsTokenFromLocation = (hash: string): string | null => {
  const fromHash = parseViewAsTokenFromHash(hash)
  if (fromHash) {
    writePendingPortalViewAsToken(fromHash)
    return fromHash
  }
  return readPendingPortalViewAsToken()
}

export const getValidStoredPortalViewAsSession = (nowMs = Date.now()): PortalViewAsStoredSession | null => {
  const stored = readPortalViewAsSession()
  if (!stored || isPortalViewAsSessionExpired(stored.expiresAt, nowMs)) {
    return null
  }
  return stored
}

export const readPendingPortalViewAsTicketId = (): string | null => {
  try {
    const ticketId = sessionStorage.getItem(PORTAL_VIEW_AS_PENDING_TICKET_KEY)?.trim()
    return ticketId && ticketId.length > 0 ? ticketId : null
  } catch {
    return null
  }
}

export const writePendingPortalViewAsTicketId = (ticketId: string): void => {
  try {
    sessionStorage.setItem(PORTAL_VIEW_AS_PENDING_TICKET_KEY, ticketId)
  } catch {
    // ignore quota / private mode
  }
}

export const clearPendingPortalViewAsTicketId = (): void => {
  try {
    sessionStorage.removeItem(PORTAL_VIEW_AS_PENDING_TICKET_KEY)
  } catch {
    // ignore quota / private mode
  }
}
